import React, { useState, useRef, useEffect, useCallback } from "react";
import { resetUserPassword } from "../../services/userService";
import {
  getLinked3DUser,
  reset3DUserPassword,
} from "../../services/users3dService";
import {
  getNotifications,
  markNotificationRead,
} from "../../services/userService";
import { createSsoToken } from "../../services/authService";
import { toast } from "../../utils/toast";
import ThemeSettings from "./ThemeSettings";

// The 3D system is deployed separately; its URL comes from the environment so
// each deployment can point at its own instance.
const THREE_D_SYSTEM_URL = (process.env.REACT_APP_3D_SYSTEM_URL || "").trim();

const Header = ({ user, onLogout, onToggleSidebar, sidebarExpanded }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState("cms"); // 'cms' | '3d'
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [launching3D, setLaunching3D] = useState(false);
  const [checking3DLink, setChecking3DLink] = useState(false);
  const [linked3DUser, setLinked3DUser] = useState(null);
  const [linkMessage, setLinkMessage] = useState("");
  const dropdownRef = useRef(null);

  // ── Notifications state ──────────────────────────────────────────────────
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [markingId, setMarkingId] = useState(null);
  const notifRef = useRef(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    const uid = user?.user_id;
    if (!uid) return;
    try {
      setNotifLoading(true);
      const data = await getNotifications(uid);
      setNotifications(data);
    } catch (e) {
      // silently fail — don't spam toasts for background polling
    } finally {
      setNotifLoading(false);
    }
  }, [user?.user_id]);

  // Fetch once on login/mount — manual refresh via the ↻ button
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close both panels on outside click — single listener for both refs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkRead = async (notif) => {
    if (notif.is_read || markingId === notif.id) return;
    try {
      setMarkingId(notif.id);
      await markNotificationRead(notif.id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n))
      );
    } catch (e) {
      toast.error("Could not mark notification as read");
    } finally {
      setMarkingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter((n) => !n.is_read);
    if (!unread.length) return;
    try {
      await Promise.all(unread.map((n) => markNotificationRead(n.id)));
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (e) {
      toast.error("Could not mark all as read");
    }
  };

  const formatNotifTime = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
  };
  // ────────────────────────────────────────────────────────────────────────

  const openProfile = () => {
    setDropdownOpen(false);
    setProfileModalOpen(true);
  };

  const openChangePassword = () => {
    setDropdownOpen(false);
    setPasswordTarget("cms");
    setPasswordForm({ password: "", confirmPassword: "" });
    setPasswordError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setLinked3DUser(null);
    setLinkMessage("");
    setChangePasswordModalOpen(true);
  };

  // Check if the logged-in CMS user is linked to a 3D user
  useEffect(() => {
    const fetchLinked3DUser = async () => {
      const cmsId = user?.user_id;
      if (!cmsId) {
        setLinked3DUser(null);
        setLinkMessage("Your account is not linked to a 3D user.");
        return;
      }
      try {
        setChecking3DLink(true);
        const linked = await getLinked3DUser(cmsId);
        if (linked) {
          setLinked3DUser(linked);
          setLinkMessage("");
        } else {
          setLinked3DUser(null);
          setLinkMessage("Your account is not linked to a 3D user.");
        }
      } catch (e) {
        setLinked3DUser(null);
        setLinkMessage(e?.message || "Failed to check 3D link");
      } finally {
        setChecking3DLink(false);
      }
    };

    if (changePasswordModalOpen && passwordTarget === "3d") {
      fetchLinked3DUser();
    }
  }, [changePasswordModalOpen, passwordTarget, user?.user_id]);

  const handleChangePassword = async () => {
    setPasswordError("");
    const password = (passwordForm.password || "").trim();
    const confirmPassword = (passwordForm.confirmPassword || "").trim();

    if (!password) {
      setPasswordError("Password is required");
      return;
    }
    if (!confirmPassword) {
      setPasswordError("Confirm password is required");
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    setSaving(true);
    try {
      if (passwordTarget === "3d") {
        if (checking3DLink) {
          setPasswordError("Checking linked 3D user...");
          toast.error("Checking linked 3D user...");
          return;
        }
        if (!linked3DUser) {
          const msg = linkMessage || "Your account is not linked to a 3D user.";
          setPasswordError(msg);
          toast.error(msg);
          return;
        }
        const targetId =
          linked3DUser.id ?? linked3DUser.user_3d_id ?? linked3DUser.user3d_id;
        if (!targetId) {
          setPasswordError("Unable to resolve linked 3D user id.");
          toast.error("Unable to resolve linked 3D user id.");
          return;
        }
        await reset3DUserPassword(targetId, { password, confirmPassword });
        toast.success("3D user password updated successfully");
      } else {
        await resetUserPassword(user.user_id, { password, confirmPassword });
        toast.success("CMS user password updated successfully");
      }
      setChangePasswordModalOpen(false);
      setPasswordForm({ password: "", confirmPassword: "" });
    } catch (e) {
      const msg = e?.message || "Failed to change password";
      setPasswordError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    onLogout();
  };

  const handleLaunch3D = async () => {
    if (launching3D) return;

    // Open synchronously so browsers do not block the tab after the API call.
    const ssoWindow = window.open("about:blank", "_blank");
    if (!ssoWindow) {
      toast.error("Allow pop-ups to open the 3D System in a new tab.");
      return;
    }
    ssoWindow.opener = null;

    setLaunching3D(true);
    try {
      const token = await createSsoToken();
      const ssoUrl = new URL("/sso", THREE_D_SYSTEM_URL);
      ssoUrl.searchParams.set("token", token);
      ssoWindow.location.replace(ssoUrl.toString());
    } catch (error) {
      const linked3DUserMissing =
        error?.status === 401 &&
        /3d user not found/i.test(String(error?.message || ""));

      if (linked3DUserMissing) {
        ssoWindow.location.replace(THREE_D_SYSTEM_URL);
        return;
      }

      ssoWindow.close();
      const message =
        error?.status === 401 ?
          "Your session has expired. Please sign in again."
        : error?.message || "Unable to open the 3D System";
      toast.error(message);
    } finally {
      setLaunching3D(false);
    }
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Format role for display
  const formatRole = (role) => {
    if (!role) return "User";
    return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <>
      <header className="dashboard-header">
        <div className="header-left">
          <button
            className="sidebar-toggle"
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            aria-controls="app-sidebar"
            aria-expanded={sidebarExpanded}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M3 6h18M3 12h18M3 18h18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <h1 className="header-title">CMS Dashboard</h1>
        </div>

        <div className="header-right">
          {THREE_D_SYSTEM_URL && (
            <button
              type="button"
              className="launch-3d-btn"
              onClick={handleLaunch3D}
              disabled={launching3D}
              aria-busy={launching3D}
              title="Open the 3D System using single sign-on"
            >
              <svg
                className="launch-3d-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2l9 5v10l-9 5-9-5V7l9-5zm0 2.3L5.5 7.9 12 11.5l6.5-3.6L12 4.3zM5 9.6v6.2l6 3.3v-6.2L5 9.6zm8 9.5l6-3.3V9.6l-6 3.3v6.2z"
                  fill="currentColor"
                />
              </svg>
              <span className="launch-3d-label">
                {launching3D ? "Opening..." : "3D System"}
              </span>
              <svg
                className="launch-3d-external"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* ── Notification Bell ───────────────────────────────────────── */}
          <div className="notif-wrapper" ref={notifRef}>
            <button
              className="notif-bell-btn"
              onClick={() => setNotifOpen((o) => !o)}
              aria-label="Notifications"
              title="Notifications"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="notif-badge">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="notif-panel">
                {/* Panel header */}
                <div className="notif-panel-header">
                  <span className="notif-panel-title">
                    Notifications
                    {unreadCount > 0 && (
                      <span className="notif-count-chip">{unreadCount} new</span>
                    )}
                  </span>
                  {unreadCount > 0 && (
                    <button
                      className="notif-mark-all-btn"
                      onClick={handleMarkAllRead}
                    >
                      Mark all read
                    </button>
                  )}
                </div>

                {/* Panel body */}
                <div className="notif-panel-body">
                  {notifLoading && notifications.length === 0 ? (
                    <div className="notif-empty">
                      <div className="notif-spinner" />
                      <span>Loading…</span>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="notif-empty">
                      <svg
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      <span>No notifications yet</span>
                    </div>
                  ) : (
                    <ul className="notif-list">
                      {notifications.map((notif) => (
                        <li
                          key={notif.id}
                          className={`notif-item ${notif.is_read ? "read" : "unread"}`}
                          onClick={() => handleMarkRead(notif)}
                        >
                          <div className="notif-item-dot" />
                          <div className="notif-item-body">
                            <p className="notif-message">{notif.message}</p>
                            <span className="notif-time">
                              {formatNotifTime(notif.created_at)}
                            </span>
                          </div>
                          {!notif.is_read && (
                            <span className="notif-unread-pill">New</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Panel footer */}
                {notifications.length > 0 && (
                  <div className="notif-panel-footer">
                    <button
                      className="notif-refresh-btn"
                      onClick={fetchNotifications}
                      disabled={notifLoading}
                    >
                      {notifLoading ? "Refreshing…" : "↻ Refresh"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* ─────────────────────────────────────────────────────────────── */}

          <ThemeSettings />

          <div className="user-profile-dropdown" ref={dropdownRef}>
            <button
              className="user-profile-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              style={{ position: "relative" }}
            >
              <div className="user-avatar">{getInitials(user?.name)}</div>
              <span className="user-display-name">{user?.name || "User"}</span>
              <span className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}>
                ▼
              </span>
            </button>

            {dropdownOpen && (
              <div className="user-dropdown-menu">
                <div className="dropdown-user-info">
                  <div className="dropdown-avatar">
                    {getInitials(user?.name)}
                  </div>
                  <div className="dropdown-user-details">
                    <span className="dropdown-user-name">
                      {user?.name || "User"}
                    </span>
                    <span className="dropdown-user-email">
                      {user?.email || ""}
                    </span>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item" onClick={openProfile}>
                  <svg
                    className="dropdown-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Profile
                </button>
                <button className="dropdown-item" onClick={openChangePassword}>
                  <svg
                    className="dropdown-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Change Password
                </button>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout" onClick={handleLogout}>
                  <svg
                    className="dropdown-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Profile Modal */}
      {profileModalOpen && (
        <div
          className="header-modal-overlay"
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="header-modal profile-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="header-modal-header">
              <h3>Profile</h3>
              <button
                className="header-modal-close"
                onClick={() => setProfileModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="header-modal-body">
              <div className="profile-avatar-large">
                {getInitials(user?.name)}
              </div>
              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <label>Name</label>
                  <span>{user?.name || "-"}</span>
                </div>
                <div className="profile-info-item">
                  <label>Email</label>
                  <span>{user?.email || "-"}</span>
                </div>
                <div className="profile-info-item">
                  <label>Role</label>
                  <span className="role-badge">{formatRole(user?.role)}</span>
                </div>
                {user?.company_name && (
                  <div className="profile-info-item">
                    <label>Company</label>
                    <span>{user.company_name}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="header-modal-footer">
              <button
                className="secondary-btn"
                onClick={() => setProfileModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changePasswordModalOpen && (
        <div
          className="header-modal-overlay"
          onClick={() => setChangePasswordModalOpen(false)}
        >
          <div
            className="header-modal password-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="header-modal-header">
              <h3>Change Password</h3>
              <button
                className="header-modal-close"
                onClick={() => setChangePasswordModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="header-modal-body">
              <div className="password-target-toggle">
                <button
                  className={`toggle-btn ${passwordTarget === "cms" ? "active" : ""}`}
                  onClick={() => setPasswordTarget("cms")}
                >
                  Change CMS User Password
                </button>
                <button
                  className={`toggle-btn ${passwordTarget === "3d" ? "active" : ""}`}
                  onClick={() => setPasswordTarget("3d")}
                >
                  Change 3D User Password
                </button>
              </div>

              {passwordTarget === "3d" && (
                <div className="password-info">
                  {checking3DLink ?
                    <span>Checking linked 3D user...</span>
                  : linkMessage ?
                    <span className="password-error">{linkMessage}</span>
                  : linked3DUser ?
                    <span>
                      Updating linked 3D user:{" "}
                      {linked3DUser.name ||
                        linked3DUser.email ||
                        `ID ${linked3DUser.id}`}
                    </span>
                  : null}
                </div>
              )}

              {passwordError && (
                <div className="password-error">{passwordError}</div>
              )}
              <div className="password-field">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-input"
                    value={passwordForm.password}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        password: e.target.value,
                      })
                    }
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {showPassword ?
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      : <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      }
                    </svg>
                  </button>
                </div>
              </div>
              <div className="password-field">
                <label>Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    className="form-input"
                    value={passwordForm.confirmPassword}
                    onChange={(e) =>
                      setPasswordForm({
                        ...passwordForm,
                        confirmPassword: e.target.value,
                      })
                    }
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {showConfirmPassword ?
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      : <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      }
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="header-modal-footer">
              <button
                className="secondary-btn"
                onClick={() => setChangePasswordModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={handleChangePassword}
                disabled={
                  saving ||
                  (passwordTarget === "3d" && (checking3DLink || !linked3DUser))
                }
              >
                {saving ? "Saving..." : "Change Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
